import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { 
  Users, 
  Package, 
  TrendingUp, 
  AlertCircle,
  Activity,
  DollarSign,
  ShoppingCart,
  Shield,
  HeadphonesIcon,
  BarChart3,
  UserPlus,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

// GraphQL query to get all dashboard metrics
const GET_DASHBOARD_METRICS = gql`
  query GetDashboardMetrics {
    platformMetrics {
      totalUsers
      totalGroups
      activeGroups
      totalProducts
      totalPurchases
      totalSavings
      totalRevenue
      newUsers
    }
    systemHealth {
      status
      cpuUsage
      memoryUsage
      uptime
    }
    supportTickets(status: "OPEN") {
      id
    }
    securityLogs(limit: 5) {
      id
      eventType
      userId
      userEmail
      description
      severity
      timestamp
    }
  }
`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { loading, error, data, refetch } = useQuery(GET_DASHBOARD_METRICS, {
    pollInterval: 30000 // Refresh every 30 seconds
  });

  const metrics = data?.platformMetrics || {};
  const systemHealth = data?.systemHealth || {};
  const openTickets = data?.supportTickets?.length || 0;
  const recentSecurityLogs = data?.securityLogs || [];

  // Calculate security alerts (high and critical severity)
  const securityAlerts = recentSecurityLogs.filter(
    log => log.severity === 'high' || log.severity === 'critical'
  ).length;

  const getSystemStatusColor = (status) => {
    switch(status) {
      case 'healthy': return 'text-green-500';
      case 'degraded': return 'text-yellow-500';
      case 'critical': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getSystemStatusIcon = (status) => {
    switch(status) {
      case 'healthy': return <CheckCircle size={20} className="text-green-500" />;
      case 'degraded': return <AlertCircle size={20} className="text-yellow-500" />;
      case 'critical': return <XCircle size={20} className="text-red-500" />;
      default: return <Activity size={20} className="text-gray-500" />;
    }
  };

  const MetricCard = ({ title, value, icon: Icon, color, subtitle, onClick, trend }) => (
    <div 
      className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <Icon size={24} className={`text-${color}-500`} />
        </div>
        {trend && (
          <span className={`text-sm ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="text-2xl font-bold">{value || 0}</div>
      <div className="text-sm text-gray-600 mt-1">{title}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) {
      return `${Math.floor(diff / 60000)}m ago`;
    } else if (diff < 86400000) {
      return `${Math.floor(diff / 3600000)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600 mt-2">Platform overview and quick stats</p>
      </div>

      {/* System Status Bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {getSystemStatusIcon(systemHealth.status)}
              <span className="font-medium">System Status:</span>
              <span className={`font-bold ${getSystemStatusColor(systemHealth.status)}`}>
                {systemHealth.status?.toUpperCase() || 'UNKNOWN'}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Uptime: {systemHealth.uptime || 'N/A'}
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div>CPU: {systemHealth.cpuUsage || 0}%</div>
            <div>Memory: {systemHealth.memoryUsage || 0}%</div>
          </div>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Users"
          value={metrics.totalUsers}
          icon={Users}
          color="blue"
          subtitle={`+${metrics.newUsers || 0} new this week`}
          onClick={() => navigate('/admin/users')}
        />
        <MetricCard
          title="Active Groups"
          value={metrics.activeGroups}
          icon={Users}
          color="green"
          subtitle={`${metrics.totalGroups || 0} total groups`}
          onClick={() => navigate('/admin/analytics')}
        />
        <MetricCard
          title="Total Revenue"
          value={`$${(metrics.totalRevenue || 0).toFixed(2)}`}
          icon={DollarSign}
          color="purple"
          onClick={() => navigate('/admin/analytics')}
        />
        <MetricCard
          title="Total Savings"
          value={`$${(metrics.totalSavings || 0).toFixed(2)}`}
          icon={TrendingUp}
          color="yellow"
          subtitle="Platform-wide savings"
          onClick={() => navigate('/admin/analytics')}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Products"
          value={metrics.totalProducts}
          icon={Package}
          color="indigo"
          onClick={() => navigate('/admin/products')}
        />
        <MetricCard
          title="Orders"
          value={metrics.totalPurchases}
          icon={ShoppingCart}
          color="pink"
          onClick={() => navigate('/admin/orders')}
        />
        <MetricCard
          title="Open Tickets"
          value={openTickets}
          icon={HeadphonesIcon}
          color={openTickets > 5 ? "red" : "gray"}
          onClick={() => navigate('/admin/support')}
        />
        <MetricCard
          title="Security Alerts"
          value={securityAlerts}
          icon={Shield}
          color={securityAlerts > 0 ? "orange" : "gray"}
          onClick={() => navigate('/admin/security')}
        />
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-2 gap-6">
        {/* Recent Security Events */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold">Recent Security Events</h2>
            <button
              onClick={() => navigate('/admin/security')}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              View All →
            </button>
          </div>
          <div className="p-6">
            {recentSecurityLogs.length > 0 ? (
              <div className="space-y-3">
                {recentSecurityLogs.slice(0, 5).map((log) => (
                  <div key={log.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        log.severity === 'critical' ? 'bg-red-500' :
                        log.severity === 'high' ? 'bg-orange-500' :
                        log.severity === 'warning' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      }`} />
                      <span className="font-medium">{log.eventType}</span>
                    </div>
                    <span className="text-gray-500">{formatTimestamp(log.timestamp)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent security events</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">Quick Actions</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/admin/users')}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
              >
                <Users size={20} className="text-blue-500 mb-2" />
                <div className="font-medium">Manage Users</div>
              </button>
              <button
                onClick={() => navigate('/admin/products')}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
              >
                <Package size={20} className="text-green-500 mb-2" />
                <div className="font-medium">Add Product</div>
              </button>
              <button
                onClick={() => navigate('/admin/support')}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
              >
                <HeadphonesIcon size={20} className="text-purple-500 mb-2" />
                <div className="font-medium">View Tickets</div>
              </button>
              <button
                onClick={() => navigate('/admin/analytics')}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
              >
                <BarChart3 size={20} className="text-yellow-500 mb-2" />
                <div className="font-medium">Analytics</div>
              </button>
              <button
                onClick={() => navigate('/admin/orders')}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
              >
                <ShoppingCart size={20} className="text-pink-500 mb-2" />
                <div className="font-medium">View Orders</div>
              </button>
              <button
                onClick={() => navigate('/admin/groups')}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
              >
                <Users size={20} className="text-teal-500 mb-2" />
                <div className="font-medium">Manage Groups</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;